<template>
    <div class="session-termination">
        <div class="session-termination-header">
            <SupsisButton class="ai-suggest-button" @click="handleAIRecommendation" :disabled="copilotWaiting" v-if="!aiRecommendation">
                <div class="rotating-border rotating-border--rainbow" v-if="copilotWaiting">
                    <img src="/static/img/copilot.png"  class="copilot-icon" />
                </div>
                <img v-else src="/static/img/copilot.png"  class="copilot-icon" />
                {{ $t("message.aiSuggestion") }}
            </SupsisButton>
        </div>
        <div class="session-termination-content">
            <!-- AI Öneri Bölümü -->
            <div v-if="aiRecommendation && !allRecommendationsApplied" class="ai-recommendation-section">
                <div class="ai-header">
                    <div class="ai-header-left">
                        <img src="/static/img/copilot.png" width="17" height="17" class="copilot-icon" />
                        <h3>{{ $t("message.aiSuggestionTitle") }}</h3>
                    </div>
                    <SupsisButton class="ai-action-button apply-all-button" @click="applyAllRecommendations">
                        <span class="material-icons">auto_fix_high</span>
                        {{ $t("message.applyAll") }}
                    </SupsisButton>
                </div>

                <div class="ai-recommendation-item">
                    <div class="ai-content">
                        <h4>{{ $t("message.suggestedNote") }}</h4>
                        <p>{{ aiRecommendation.note }}</p>
                    </div>
                    <SupsisButton class="ai-action-button" @click="applyNoteRecommendation">{{ $t("ticimax.apply") }}</SupsisButton>
                </div>

                <div class="ai-recommendation-item" v-if="aiRecommendation.tags.length">
                    <div class="ai-content">
                        <h4>{{ $t("message.suggestedTags") }}</h4>
                        <div class="tags-container">
                            <span
                                v-for="tag in getRecommendedTags"
                                :key="tag._id"
                                :style="getTagStyle(tag.color)"
                                class="tag-label"
                            >
                                {{ tag.name }}
                            </span>
                        </div>
                    </div>
                    <SupsisButton class="ai-action-button" @click="applyTagRecommendation">{{ $t("ticimax.apply") }}</SupsisButton>
                </div>

                <div class="ai-recommendation-item" v-if="aiRecommendation.contactProperties.length">
                    <div class="ai-content">
                        <h4>{{ $t("message.suggestedProfileFields") }}</h4>
                        <div class="property-list">
                            <div
                                v-for="prop in mappedPropertyRecommendations"
                                :key="prop.propertyId"
                                class="property-item"
                            >
                                <span class="prop-name">{{ getPropertyName(prop.propertyId) }}:</span>
                                <span class="prop-value">{{ prop.propertyValue }}</span>
                            </div>
                        </div>
                    </div>
                    <SupsisButton class="ai-action-button" @click="applyPropertyRecommendation">{{ $t("ticimax.apply") }}</SupsisButton>
                </div>
            </div>

            <div class="section-row">
                <!-- Not Ekleme Alanı -->
                <div class="section notes-section">
                    <div class="section-header">
                        <div class="section-title">
                            <span class="material-icons">note_add</span>
                            {{ $t("message.addNote") }}
                        </div>
                        <div class="section-description">
                            {{ $t("message.noteDescription") }}
                        </div>
                    </div>
                    <a-textarea v-model="note" :placeholder="$t('message.enterNote')" :auto-size="{ minRows: 2, maxRows: 4 }" />
                    <div class="note-privacy">
                        <a-checkbox v-model="isPrivateNote"> {{ $t("message.privateNote") }} </a-checkbox>
                    </div>
                </div>

                <!-- Etiket Ekleme/Çıkarma -->
                <div class="section tags-section">
                    <div class="section-header">
                        <div class="section-title">
                            <span class="material-icons">label</span>
                            {{ $t("message.manageTags") }}
                        </div>
                        <div class="section-description">
                           {{ $t("message.categoryTags") }}
                        </div>
                    </div>
                    <div class="tags-container">
                        <span
                            v-for="tag in mappedTags"
                            :key="tag._id"
                            :style="getTagStyle(tag.color)"
                            class="tag-label"
                        >
                            <span class="remove-tag" @click="removeTag(tag._id)">×</span>
                            {{ tag.name }}
                        </span>
                    </div>
                    <a-button type="dashed" icon="plus" @click="showTagDialog = true" style="margin-top: 8px">
                        {{ $t("message.addTag") }}
                    </a-button>
                </div>
            </div>

            <!-- Contact Properties -->
            <div class="section properties-section" v-if="contactProperties.length > 0">
                <div class="section-header">
                    <div class="section-title">
                        <span class="material-icons">contact_mail</span>
                        {{ $t("visitor.profileFields") }}
                    </div>
                    <div class="section-description">
                       {{ $t("message.profileFieldsDescription") }}
                    </div>
                </div>
                <div class="profile-fields">
                    <ContactProperties
                        ref="contactPropertiesRef"
                        :contact="contact"
                        :show-widget-title="false"
                        :columns="3"
                        :draggable="false"
                        :orderable="false"
                        :max-visible-rows="2"
                        :show-more="showMoreProperties"
                        :show-info="false"
                    />
                    <div class="show-more-container" v-if="contactProperties && contactProperties.length > 6">
                        <a-button type="link" @click="toggleShowMore">
                            {{ showMoreProperties ? $t("message.showLess") : $t("message.showMore") }}
                            <span class="material-icons">{{ showMoreProperties ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</span>
                        </a-button>
                    </div>
                </div>
            </div>
        </div>

        <div class="session-termination-footer">
            <a-button @click="handleCancel"> {{ $t("message.cancelText") }} </a-button>
            <a-button type="primary" :loading="isTerminating" @click="handleTerminate"> {{ $t("message.endChat") }} </a-button>
        </div>

        <Modal2 v-if="showTagDialog" :title="$t('message.manageTags')" @cancel="showTagDialog = false" :showFooter="false">
            <TagDialog
                :value="selectedTagIds"
                :inlineMode="true"
                @input="handleTagUpdate"
                @close="showTagDialog = false"
            />
        </Modal2>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import TagDialog from "../../TagDialog.vue";
import ContactDetailItem from "./ContactDetailItem.vue";
import TagSvg from "./svg/TagSvg.vue";
import Modal2 from "../../common/Modal2.vue";
import ContactProperties from "./ContactProperties.vue";
import _ from "lodash";
import CoPilot from "../../../utils/CoPilot";
import SupsisButton from "../../../views/Pages/Settings/components/SupsisButton.vue";

export default {
    name: "SessionTermination",
    components: {
        TagDialog,
        ContactDetailItem,
        TagSvg,
        Modal2,
        ContactProperties,
        SupsisButton,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            note: "",
            isPrivateNote: false,
            selectedTagIds: [],
            propValues: {},
            isTerminating: false,
            showTagDialog: false,
            copilotWaiting: false,
            aiRecommendation: null,
            allRecommendationsApplied: false,
            showMoreProperties: false,
        };
    },
    computed: {
        ...mapState({
            tags: (state) => state.tag.tags.data,
            contactProperties: (state) => state.contactProperty.data,
        }),
        mappedTags() {
            return this.selectedTagIds.map((tagId) => this.tags.find((tag) => tag._id === tagId)).filter((tag) => tag);
        },
        getRecommendedTags() {
            return this.aiRecommendation.tags.map((tagId) => this.tags.find((t) => t._id === tagId)).filter((t) => t);
        },
        mappedPropertyRecommendations() {
            return this.aiRecommendation.contactProperties
                .map((rec) => ({
                    ...rec,
                    property: this.contactProperties.find((p) => p._id === rec.propertyId),
                }))
                .filter((p) => p.property);
        },
    },
    methods: {
        ...mapActions({
            createNote: "visitor/createVisitorNote",
            updateContactProperty: "visitor/updateVisitorAttr",
            updateContactFields: "contact/updateContactFields",
            updateVisitorTags: "visitor/updateTagVisitor",
            createContactProperty: "visitor/createContactProperty",
        }),
        async handleTerminate() {
            this.isTerminating = true;
            try {
                // Not ekleme
                if (this.note.trim()) {
                    await this.createNote({
                        visitorId: this.contact._id,
                        payload: {
                            description: this.note,
                            isPrivate: this.isPrivateNote,
                        },
                    });
                }

                const oldTagIds = this.contact.tags.map((tag) => (typeof tag === "string" ? tag : tag._id));
                // Etiket güncelleme - sadece değişiklik varsa
                if (!_.isEqual(this.selectedTagIds, oldTagIds)) {
                    await this.updateContactFields({
                        contactId: this.contact._id,
                        set: {
                            tags: this.selectedTagIds,
                        },
                    });
                    this.updateVisitorTags({
                        visitorId: this.contact._id,
                        tagIds: this.selectedTagIds,
                    });
                }

                this.$emit("terminate");
            } catch (error) {
                this.$message.error(this.$t("message.terminationError"));
            } finally {
                this.isTerminating = false;
                this.note = "";
                this.isPrivateNote = false;
            }
        },
        handleCancel() {
            this.$emit("cancel");
        },
        handleTagUpdate(newTags) {
            this.selectedTagIds = newTags;
            this.showTagDialog = false;
        },
        removeTag(tagId) {
            this.selectedTagIds = this.selectedTagIds.filter((id) => id !== tagId);
        },
        getTagStyle(color) {
            const isDark = this.isDarkColor(color);
            return {
                backgroundColor: this.lightenColor(color, 0.3),
                color: isDark ? "white" : "black",
                borderRadius: "12px",
                padding: "2px 8px",
                marginRight: "5px",
                display: "inline-flex",
                alignItems: "center",
                maxWidth: "100%",
                flexShrink: 0,
            };
        },
        isDarkColor(color) {
            const c = color.substring(1); // strip #
            const rgb = parseInt(c, 16); // convert rrggbb to decimal
            const r = (rgb >> 16) & 0xff; // extract red
            const g = (rgb >> 8) & 0xff; // extract green
            const b = (rgb >> 0) & 0xff; // extract blue
            const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
            return luma < 128;
        },
        lightenColor(color, percent) {
            const num = parseInt(color.slice(1), 16),
                amt = Math.round(2.55 * percent),
                R = (num >> 16) + amt,
                G = ((num >> 8) & 0x00ff) + amt,
                B = (num & 0x0000ff) + amt;
            return `#${(
                0x1000000 +
                (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
                (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
                (B < 255 ? (B < 1 ? 0 : B) : 255)
            )
                .toString(16)
                .slice(1)}`;
        },
        async handleAIRecommendation() {
            try {
                this.copilotWaiting = true;
                const copilot = CoPilot.getInstance();
                const recommendation = await copilot.getSessionRecommendations({
                    siteId: this.$store.state.site.site._id,
                    conversationId: this.contact.lastConversation?._id,
                    contactId: this.contact._id,
                    contactName: this.contact.fullname,
                    phone: this.contact.phone || this.contact.whatsappNumber,
                    email: this.contact.email,
                    platform: this.contact.platform,

                    currentProfileFieldOfContact: (this.contact?.contactProperties || []).map((x) => ({
                        propertyId: x.property,
                        value: x.value,
                    })),
                    currentTagsOfContact: this.contact.tags,
                    allProfileFieldMetas: this.contactProperties,
                    allTagMetas: this.tags,
                });
                if (!recommendation) {
                    this.$message.error(this.$t("message.aiSuggestionError"));
                    this.copilotWaiting = false;
                    return;
                }
                this.aiRecommendation = recommendation;
            } catch (error) {
                this.$message.error(this.$t("message.aiSuggestionError"));
            } finally {
                this.copilotWaiting = false;
            }
        },
        getPropertyName(propertyId) {
            return this.contactProperties.find((p) => p._id === propertyId)?.name || propertyId;
        },
        applyNoteRecommendation() {
            this.note = this.aiRecommendation.note;
        },
        async applyTagRecommendation() {
            if (this.aiRecommendation.tags.length === 0) {
                return;
            }
            const newTags = _.uniq([...this.selectedTagIds, ...this.aiRecommendation.tags]);
            this.selectedTagIds = newTags;
        },
        async applyPropertyRecommendation() {
            try {
                if (this.aiRecommendation.contactProperties.length === 0) {
                    return;
                }
                const updates = this.aiRecommendation.contactProperties.map((prop) => ({
                    property: prop.propertyId,
                    value: prop.propertyValue,
                }));

                for (const update of updates) {
                    await this.createContactProperty({
                        visitorId: this.contact._id,
                        payload: {
                            property: update.property,
                            value: update.value,
                        },
                    });
                }

                await this.updateContactFields({
                    contactId: this.contact._id,
                    set: {
                        contactProperties: [...this.contact.contactProperties, ...updates],
                    },
                });
                this.$message.success(this.$t("message.profileFieldsUpdated"));
            } catch (error) {
                this.$message.error(this.$t("message.profileFieldsUpdateError"));
            }
        },
        async applyAllRecommendations() {
            this.applyNoteRecommendation();
            await this.applyTagRecommendation();
            await this.applyPropertyRecommendation();
            this.allRecommendationsApplied = true;
        },
        toggleShowMore() {
            this.showMoreProperties = !this.showMoreProperties;
        },
    },
    created() {
        // Mevcut etiketleri seç
        if (this.contact.tags) {
            this.selectedTagIds = this.contact.tags.map((tag) => (typeof tag === "string" ? tag : tag._id));
        }
    },
};
</script>

<style lang="scss" scoped>
.session-termination {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &-header {
        h3 {
            margin: 0;
            color: #333;
        }
    }

    &-content {
        display: flex;
        flex-direction: column;
        gap: 20px;

        .section-row {
            display: flex;
            gap: 20px;

            .section {
                flex: 1;
            }
        }

        .section {
            border: 1px solid #e8e8e8;
            border-radius: 4px;
            padding: 16px;

            &-header {
                margin-bottom: 12px;
            }

            &-title {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 12px;
                font-weight: 600;
                color: #000000;
                font-size: 20px;

                .material-icons {
                    font-size: 20px;
                }
            }

            &-description {
                font-size: 12px;
                color: #999;
            }
        }

        .notes-section {
            .note-privacy {
                margin-top: 8px;
            }
        }

        .tags-section {
            .section-title {
                margin-bottom: 8px;
            }
        }

        .properties-section {
            .property-item {
                margin-bottom: 12px;
                display: flex;
                flex-direction: column;
                gap: 4px;

                .property-label {
                    font-size: 13px;
                    color: #666;
                }
            }
        }
    }

    &-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 20px;
    }
}

.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-bottom: 8px;
    min-height: 32px; // Etiket yokken de alan oluşturmak için
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 4px;
}

.tag-label {
    display: inline-flex;
    align-items: center;
    border-radius: 12px;
    padding: 2px 8px;
    margin-right: 5px;
    font-size: 12px;
    max-width: 100%;
    flex-shrink: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .remove-tag {
        margin-right: 5px;
        cursor: pointer;
        font-weight: bold;
    }
}

.ai-recommendation-section {
    background: linear-gradient(145deg, #f8f9fa, #ffffff);
    border: 2px solid;
    border-image: linear-gradient(45deg, #6c5ce7, #a66efa, #ff6b6b, #6c5ce7) 1;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 4px 20px rgba(108, 92, 231, 0.15);
    animation: slideIn 0.5s ease-out, borderFlow 3s linear infinite;

    .ai-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 20px;

        .ai-header-left {
            display: flex;
            align-items: center;
            gap: 10px;

            img {
                vertical-align: middle;
            }

            h3 {
                margin: 0;
                color: #6c5ce7;
                font-size: 1.4em;
                line-height: 17px;
            }
        }

        .apply-all-button {
            min-width: 150px;

            .material-icons {
                margin-right: 8px;
            }
        }
    }

    .ai-recommendation-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px;
        margin: 10px 0;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        transition: transform 0.2s;

        &:hover {
            transform: translateY(-2px);
        }

        .ai-content {
            flex: 1;
            margin-right: 20px;

            h4 {
                color: #2d3436;
                margin-bottom: 8px;
                font-size: 1.1em;
            }

            p {
                color: #636e72;
                margin: 0;
            }

            .property-list {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                gap: 10px;

                .property-item {
                    background: #f8f9fa;
                    padding: 8px;
                    border-radius: 6px;
                    display: flex;
                    justify-content: space-between;

                    .prop-name {
                        font-weight: 500;
                        color: #2d3436;
                    }

                    .prop-value {
                        color: #6c5ce7;
                        font-weight: 600;
                    }
                }
            }
        }

        .ai-action-button {
            min-width: 100px;
            align-self: center;
            margin-left: auto;
        }
    }

    .ai-footer {
        margin-top: 20px;
        border-top: 1px solid #eee;
        padding-top: 15px;
        display: flex;
        justify-content: center;
        align-items: center;

        .ai-action-button {
            min-width: 150px;
        }
    }
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes borderFlow {
    0% {
        border-image: linear-gradient(45deg, #6c5ce7 0%, #a66efa 33%, #ff6b6b 66%, #6c5ce7 100%) 1;
    }
    25% {
        border-image: linear-gradient(45deg, #6c5ce7 75%, #a66efa 0%, #ff6b6b 33%, #6c5ce7 66%) 1;
    }
    50% {
        border-image: linear-gradient(45deg, #6c5ce7 50%, #a66efa 75%, #ff6b6b 0%, #6c5ce7 33%) 1;
    }
    75% {
        border-image: linear-gradient(45deg, #6c5ce7 25%, #a66efa 50%, #ff6b6b 75%, #6c5ce7 0%) 1;
    }
    100% {
        border-image: linear-gradient(45deg, #6c5ce7 0%, #a66efa 33%, #ff6b6b 66%, #6c5ce7 100%) 1;
    }
}

.copilot-icon {
    vertical-align: middle;
    margin-right: 0px;
    width: 15px;
    height: 15px;
}

.session-termination-header {
    .ant-btn {
        display: inline-flex;
        align-items: center;
    }
}

.ai-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    .ai-header-left {
        display: flex;
        align-items: center;
        gap: 10px;

        img {
            vertical-align: middle;
        }

        h3 {
            margin: 0;
            color: #6c5ce7;
            font-size: 1.4em;
            line-height: 17px;
        }
    }

    .apply-all-button {
        min-width: 150px;

        .material-icons {
            margin-right: 8px;
        }
    }
}

.ai-suggest-button,
.ai-action-button {
    background: linear-gradient(145deg, #ffffff, #f8f9fa) !important;
    border: 2px solid !important;
    color: #6c5ce7 !important;
    font-weight: 600 !important;
    transition: all 0.3s ease;
    position: relative;

    &:not(:disabled) {
        border-image: linear-gradient(45deg, #6c5ce7, #a66efa, #ff6b6b, #6c5ce7) 1 !important;
        animation: borderFlow 3s linear infinite;
    }

    &:disabled {
        border-image: linear-gradient(45deg, #6c5ce7, #a66efa, #ff6b6b, #6c5ce7) 1 !important;
        animation: borderFlow 1.5s linear infinite;
        cursor: wait;
    }

    &:hover:not(:disabled) {
        background: linear-gradient(145deg, #f8f9fa, #ffffff) !important;
        opacity: 0.9;
        transform: translateY(-1px);
    }

    .copilot-icon {
        vertical-align: middle;
    }
}

.show-more-container {
    display: flex;
    justify-content: center;
    margin-top: 10px;

    .ant-btn {
        display: flex;
        align-items: center;

        .material-icons {
            font-size: 18px;
            margin-left: 4px;
        }
    }
}

@keyframes borderFlow {
    0% {
        border-image: linear-gradient(45deg, #6c5ce7 0%, #a66efa 33%, #ff6b6b 66%, #6c5ce7 100%) 1 !important;
    }
    25% {
        border-image: linear-gradient(45deg, #6c5ce7 75%, #a66efa 0%, #ff6b6b 33%, #6c5ce7 66%) 1 !important;
    }
    50% {
        border-image: linear-gradient(45deg, #6c5ce7 50%, #a66efa 75%, #ff6b6b 0%, #6c5ce7 33%) 1 !important;
    }
    75% {
        border-image: linear-gradient(45deg, #6c5ce7 25%, #a66efa 50%, #ff6b6b 75%, #6c5ce7 0%) 1 !important;
    }
    100% {
        border-image: linear-gradient(45deg, #6c5ce7 0%, #a66efa 33%, #ff6b6b 66%, #6c5ce7 100%) 1 !important;
    }
}

.rotating-border {
    --border-radius: 1rem;
    --border-size: 2px;
    --border-bg: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);

    position: relative;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    height: 25px;

    &::before {
        content: "";
        display: block;
        background: var(--border-bg);
        width: calc(100% * 1.41421356237);
        padding-bottom: calc(100% * 1.41421356237);
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        border-radius: 100%;
        z-index: -2;
        animation: spin 5s linear infinite;
    }

    &--reverse::before {
        animation-direction: reverse;
    }

    &::after {
        content: "";
        position: absolute;
        inset: var(--border-size);
        background: white;
        z-index: -1;
        border-radius: calc(var(--border-radius) - var(--border-size));
    }

    .copilot-icon {
        position: relative;
        z-index: 1;
    }
}

.rotating-border--rainbow {
    --border-radius: 1.5rem;
    --border-size: 3px;
    --border-bg: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);
}

@keyframes spin {
    from {
        transform: translate(-50%, -50%) rotate(0);
    }
    to {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}
</style>
